import { Signal } from '@crown/signal';
import { isBoolean, isList, isNumber, isObjectLike, isString } from '../ir/guards';
import { parseJson } from '../ir/json';
import { isPlainObject } from '../ir/prop-values';
import type { JsonValue, PropRecord, PropValue } from '../ir/types';
import { InstructionError, isSignalLike } from '../ir/values';

/** Island props travel in `data-c-props` as JSON. */
type Wire =
  | string
  | number
  | boolean
  | null
  | { readonly $sig: Wire }
  | { readonly $date: string }
  | readonly Wire[]
  | { readonly [key: string]: Wire };

/**
 * Adds `key` as an own data member. Plain assignment would treat a `__proto__` key from the wire as the
 * prototype setter, so a crafted `data-c-props` could swap the record's prototype.
 */
function defineMember<V>(target: Record<string, V>, key: string, value: V): void {
  Object.defineProperty(target, key, { value, enumerable: true, writable: true, configurable: true });
}

function toWire(value: PropValue, path: string): Wire {
  if (value === null || value === undefined) return null;
  if (isString(value) || isNumber(value) || isBoolean(value)) return value;
  if (value instanceof Date) return { $date: value.toISOString() };
  if (isSignalLike(value)) return { $sig: toWire(value.get(), path) };
  if (isList(value)) return value.map((item, i) => toWire(item, `${path}[${i}]`));
  if (!isObjectLike(value) || !isPlainObject(value)) {
    throw new InstructionError(`Island props carry JSON, dates and signals only (${path})`);
  }
  const out: Record<string, Wire> = {};
  for (const [key, inner] of Object.entries(value)) {
    if (inner !== undefined) defineMember(out, key, toWire(inner, `${path}.${key}`));
  }
  return out;
}

export function serialiseProps(props: PropRecord): string {
  return JSON.stringify(toWire(props, 'props'));
}

/** What `reviveProps` hands an island: JSON values with signals and dates restored. */
export type Revived =
  | string
  | number
  | boolean
  | null
  | Date
  | Signal.State<Revived>
  | Revived[]
  | RevivedRecord;

export interface RevivedRecord {
  [key: string]: Revived;
}

function fromWire(value: JsonValue): Revived {
  if (value === null || isString(value) || isNumber(value) || isBoolean(value)) return value;
  if (isList(value)) return value.map(fromWire);
  const keys = Object.keys(value);
  const { $sig, $date } = value;
  if (keys.length === 1 && $sig !== undefined) return new Signal.State(fromWire($sig));
  if (keys.length === 1 && isString($date)) return new Date($date);
  const out: RevivedRecord = {};
  for (const [key, inner] of Object.entries(value)) defineMember(out, key, fromWire(inner));
  return out;
}

export function reviveProps(json: string): RevivedRecord {
  const parsed = parseJson(json);
  if (!parsed.ok) throw new InstructionError(`Island props are not valid JSON: ${parsed.reason}`);
  if (!isObjectLike(parsed.value) || isList(parsed.value)) {
    throw new InstructionError('Island props must be a JSON object');
  }
  const out: RevivedRecord = {};
  for (const [key, inner] of Object.entries(parsed.value)) defineMember(out, key, fromWire(inner));
  return out;
}
